// ===== BEGIN FILE: src/components/report-v2/sections/HouseCombinedSection.tsx =====
/**
 * Merged house analysis: House Strength & Zodiac Impact  +
 *                        Cumulative House Scores & Planet Impact  +
 *                        4-Metric Progress Bars (Bhava / Planet / Dasha / Transit)
 *
 * Layout: one full-width card per house (flex-row on sm+, stacked on mobile).
 * Followed by Planetary Interpretations (pihNote cards).
 */

import React from 'react';
import { Box, Typography } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';

// ── Lookup tables ───────────────────────────────────────────────────────────

const PLANET_ABBREV: Record<string, string> = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me',
  Jupiter: 'Ju', Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};

const SIGN_GLYPH: Record<string, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
};

const SIGN_ELEMENT: Record<string, 'fire' | 'earth' | 'air' | 'water'> = {
  Aries: 'fire', Leo: 'fire', Sagittarius: 'fire',
  Taurus: 'earth', Virgo: 'earth', Capricorn: 'earth',
  Gemini: 'air', Libra: 'air', Aquarius: 'air',
  Cancer: 'water', Scorpio: 'water', Pisces: 'water',
};

const ELEMENT_STYLE: Record<string, { bg: string; accent: string; text: string; glyph: string }> = {
  fire:  { bg: 'rgba(220,60,20,0.06)',  accent: '#C2410C', text: '#9A3412', glyph: '🔥' },
  earth: { bg: 'rgba(22,163,74,0.06)',  accent: '#15803D', text: '#166534', glyph: '🌱' },
  air:   { bg: 'rgba(37,99,235,0.06)',  accent: '#1D4ED8', text: '#1E40AF', glyph: '💨' },
  water: { bg: 'rgba(8,145,178,0.06)',  accent: '#0E7490', text: '#155E75', glyph: '💧' },
};

const HOUSE_MEANING: Record<number, { title: string; keywords: string }> = {
  1:  { title: 'Self & Persona',       keywords: 'Body, Personality, Vitality' },
  2:  { title: 'Wealth & Family',      keywords: 'Speech, Savings, Early life' },
  3:  { title: 'Courage & Siblings',   keywords: 'Communication, Efforts' },
  4:  { title: 'Home & Comforts',      keywords: 'Mother, Property, Happiness' },
  5:  { title: 'Intellect & Children', keywords: 'Creativity, Education, Mantra' },
  6:  { title: 'Enemies & Illness',    keywords: 'Debt, Obstacles, Service' },
  7:  { title: 'Marriage & Partners',  keywords: 'Business, Foreign, Desires' },
  8:  { title: 'Longevity & Hidden',   keywords: 'Inheritance, Occult, Secrets' },
  9:  { title: 'Fortune & Dharma',     keywords: 'Father, Teacher, Pilgrimage' },
  10: { title: 'Career & Status',      keywords: 'Authority, Profession, Fame' },
  11: { title: 'Gains & Aspirations',  keywords: 'Friends, Income, Networks' },
  12: { title: 'Moksha & Losses',      keywords: 'Isolation, Foreign, Liberation' },
};

// ── Rating themes (same thresholds used throughout report-v2) ──────────────

const THEMES = {
  excellent: { bg: '#f0fdf4', light: '#dcfce7', border: '#16a34a', accent: '#22c55e', text: '#15803d', barBg: '#bbf7d0' },
  good:      { bg: '#eff6ff', light: '#dbeafe', border: '#2563eb', accent: '#3b82f6', text: '#1d4ed8', barBg: '#bfdbfe' },
  fair:      { bg: '#fffbeb', light: '#fef3c7', border: '#d97706', accent: '#f59e0b', text: '#b45309', barBg: '#fde68a' },
  danger:    { bg: '#fef2f2', light: '#fee2e2', border: '#dc2626', accent: '#ef4444', text: '#b91c1c', barBg: '#fecaca' },
} as const;

const getThemeKey = (n: number): keyof typeof THEMES =>
  n >= 75 ? 'excellent' : n >= 60 ? 'good' : n >= 30 ? 'fair' : 'danger';

const getRatingLabel = (n: number) =>
  n >= 75 ? 'Excellent' : n >= 60 ? 'Good' : n >= 30 ? 'Fair' : 'Needs Attention';

const getBhavaLabel = (pct: number): { label: string; color: string } => {
  if (pct >= 80) return { label: 'Strong',  color: '#15803D' };
  if (pct >= 55) return { label: 'Average', color: '#CA8A04' };
  return             { label: 'Weak',    color: '#DC2626' };
};

// (storedValue − 50) × 2  →  −100..+100 with 0 = neutral
const toSignedDisplay = (v: number) => {
  const d = Math.round((v - 50) * 2);
  return (d >= 0 ? '+' : '') + d;
};

const getFuncColor = (nature: string) =>
  (nature === 'benefic' || nature === 'yoga_karaka') ? '#16a34a'
    : nature === 'malefic' ? '#dc2626'
    : '#d4b106';

// ── Component ───────────────────────────────────────────────────────────────

export const HouseCombinedSection: React.FC<ReportSectionProps> = ({ data, onNavigate }) => {
  const { houseScores, birthDetails, houseLords, lordDusthanaPenalties } = data;
  const cusps          = birthDetails.houseCusps       ?? [];
  const planetsInHouses = birthDetails.planetsInHouses ?? [];
  const aspectsData    = birthDetails.planetaryAspects ?? [];
  const timestamp      = birthDetails.houseMetricsTimestamp;

  // Functional nature map for all planets (used to colour aspectors)
  const planetFuncMap: Record<string, string> = {};
  planetsInHouses.forEach(({ planets }) =>
    planets.forEach(pl => { planetFuncMap[pl.name] = pl.functionalNature || 'neutral'; })
  );

  // Resident set per house (for aspector deduplication)
  const residentsByHouse: Record<number, Set<string>> = {};
  planetsInHouses.forEach(({ house, planets }) => {
    residentsByHouse[house] = new Set(planets.map(p => p.name));
  });

  // Aspectors per house — deduplicated, exclude residents
  const aspectorsByHouse: Record<number, { planet: string; kind: string; nature: string }[]> = {};
  for (let h = 1; h <= 12; h++) aspectorsByHouse[h] = [];
  const seenAspectors: Record<number, Set<string>> = {};
  for (let h = 1; h <= 12; h++) seenAspectors[h] = new Set();
  aspectsData.forEach(a => {
    if (!residentsByHouse[a.toHouse]?.has(a.from) && !seenAspectors[a.toHouse]?.has(a.from)) {
      aspectorsByHouse[a.toHouse].push({ planet: a.from, kind: a.kind, nature: planetFuncMap[a.from] || 'neutral' });
      seenAspectors[a.toHouse].add(a.from);
    }
  });

  // Planets with interpretive notes (for the section after the 12 cards)
  const planetInterps = planetsInHouses.flatMap(({ house, sign, planets }) =>
    planets.filter(pl => pl.pihNote).map(pl => ({ ...pl, house, sign }))
  );

  // Build per-house view model
  const houseData = Array.from({ length: 12 }, (_, i) => {
    const h     = i + 1;
    const score = houseScores[h] ?? 0;
    const cusp  = cusps.find(c => c.house === h);
    const pih   = planetsInHouses.find(p => p.house === h);
    const lord  = houseLords?.[h];

    const bhavaPct     = cusp?.bhavaPct     ?? null;
    const dashaImpact  = cusp?.dashaImpact  ?? null;
    const transitImpact = cusp?.transitImpact ?? null;
    const sign         = cusp?.sign ?? pih?.sign ?? '—';

    // Normalize Planet Impact (−50..+50) → 0-100 for bar width & weighted formula
    const normalizedPlanet = ((score + 50) / 100) * 100;
    const natalComponent   = bhavaPct != null ? (bhavaPct + normalizedPlanet) / 2 : normalizedPlanet;
    const temporalComponent = ((dashaImpact ?? 50) + (transitImpact ?? 50)) / 2;
    const combined = Math.round(natalComponent * 0.65 + temporalComponent * 0.35);

    return {
      h, score, sign, combined,
      cuspDeg:     h === 1 ? cusp?.cusp : undefined,
      signNote:    cusp?.signNote,
      bhavaPct, dashaImpact, transitImpact, normalizedPlanet,
      residents:   pih?.planets ?? [],
      lihPosition: pih?.lihPosition,
      lihNote:     pih?.lihNote,
      lordName:    lord?.lord ?? '—',
      lordHouse:   lord?.lordHouse,
      aspectors:   aspectorsByHouse[h] ?? [],
      affliction:  lordDusthanaPenalties?.[h] ?? null,
    };
  });

  return (
    <ReportSection
      title="House-by-House Analysis"
      icon="🏠"
      pageBreakBefore
      onNavigate={onNavigate ? () => onNavigate('calculator') : undefined}
      navigateLabel="View in Calculator →"
      navigateTab="calculator"
    >
      {/* ── Intro ─────────────────────────────────────────────────── */}
      <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '10px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.level1' }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.7 }}>
          The <strong>Combined %</strong> shown on each card is a weighted score of all four metrics —
          <strong> Bhav Bala</strong> (classical positional strength) +{' '}
          <strong>Planet Impact</strong> (Shadbala composite, signed) form the <em>natal component</em> (65% weight);{' '}
          <strong>Dasha Impact</strong> (functional benefic/malefic activation of current periods) +{' '}
          <strong>Transit Impact</strong> (live planetary influence) form the <em>temporal component</em> (35% weight).
        </Typography>
        {timestamp && (
          <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af', fontStyle: 'italic', mt: 0.5 }}>
            Dasha &amp; Transit metrics computed as of{' '}
            {new Date(timestamp).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}.
          </Typography>
        )}
      </Box>

      {/* ── 12 house cards ────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        {houseData.map(hd => {
          const {
            h, score, sign, combined, cuspDeg, signNote,
            bhavaPct, dashaImpact, transitImpact, normalizedPlanet,
            residents, lihPosition, lihNote, lordName, lordHouse, aspectors, affliction,
          } = hd;

          const element = SIGN_ELEMENT[sign] || 'fire';
          const eStyle  = ELEMENT_STYLE[element];
          const glyph   = SIGN_GLYPH[sign] || '';
          const meaning = HOUSE_MEANING[h];
          // Dusthana override: unprotected lord in dusthana forces 'danger' theme & Needs Attention
          const tk      = (affliction && !affliction.jupiterProtected) ? 'danger' : getThemeKey(combined);
          const theme   = THEMES[tk];
          const ratingLabel = (affliction && !affliction.jupiterProtected) ? 'Needs Attention' : getRatingLabel(combined);

          // Bar definitions — value = 0-100 for CSS width, displayLabel = human-readable
          const bars: Array<{
            label: string; value: number | null; displayLabel: string;
            isBipolar: boolean; scaleMin?: string; scaleMax?: string;
          }> = [
            {
              label: 'Bhav Bala', value: bhavaPct, isBipolar: false,
              displayLabel: bhavaPct != null ? String(Math.round(bhavaPct)) : '—',
            },
            {
              label: 'Planet', value: normalizedPlanet, isBipolar: true,
              scaleMin: '−50', scaleMax: '+50',
              displayLabel: (score >= 0 ? '+' : '') + Math.round(score),
            },
            {
              label: 'Dasha', value: dashaImpact, isBipolar: true,
              scaleMin: '−100', scaleMax: '+100',
              displayLabel: dashaImpact != null ? toSignedDisplay(dashaImpact) : '—',
            },
            {
              label: 'Transit', value: transitImpact, isBipolar: true,
              scaleMin: '−100', scaleMax: '+100',
              displayLabel: transitImpact != null ? toSignedDisplay(transitImpact) : '—',
            },
          ];

          const hasNotes = !!(lihPosition || lihNote || signNote);

          return (
            <Box
              key={h}
              sx={{
                borderRadius: '12px',
                border: `1.5px solid ${theme.light}`,
                background: `linear-gradient(145deg, ${eStyle.bg} 0%, #ffffff 65%)`,
                boxShadow: `0 2px 8px ${theme.border}10`,
                overflow: 'hidden',
              }}
            >
              {/* Accent strip */}
              <Box sx={{ height: '3px', background: `linear-gradient(90deg, ${eStyle.accent}, ${theme.border})` }} />

              {/* ── Header row ─────────────────────────────────────── */}
              <Box sx={{
                px: 1.5, pt: 1, pb: 0.75,
                display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                borderBottom: `1px solid ${theme.light}`,
              }}>
                {/* H# */}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: eStyle.accent, minWidth: 22, flexShrink: 0 }}>
                  H{h}
                </Typography>
                {/* Sign + element inline */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: eStyle.text }}>
                    {glyph} {sign}
                  </Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: eStyle.accent, bgcolor: `${eStyle.accent}12`, px: 0.6, py: 0.15, borderRadius: '6px' }}>
                    {eStyle.glyph} {element}
                  </Typography>
                </Box>
                {/* Spacer */}
                <Box sx={{ flex: 1 }} />
                {/* Combined % + rating */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: theme.text }}>{combined}%</Typography>
                  <Box sx={{ background: theme.light, borderRadius: '8px', px: 0.75, py: '2px', border: `1px solid ${theme.border}40` }}>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: theme.text }}>{ratingLabel}</Typography>
                  </Box>
                  {/* Dusthana badge */}
                  {affliction && (() => {
                    const off = (affliction.lordHouse - h + 12) % 12;
                    const nth = off === 5 ? '6th' : off === 7 ? '8th' : '12th';
                    const jp = affliction.jupiterProtected;
                    return (
                      <Box
                        title={jp ? `${affliction.reason} — protected by Jupiter aspect` : affliction.reason}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: '2px', cursor: 'help',
                          bgcolor: jp ? '#f0fdf4' : affliction.severity === 'critical' ? '#fef2f2' : affliction.severity === 'severe' ? '#fff7ed' : '#fffbeb',
                          border: `1px solid ${jp ? '#86efac' : affliction.severity === 'critical' ? '#fca5a5' : affliction.severity === 'severe' ? '#fdba74' : '#fcd34d'}`,
                          borderRadius: '5px', px: 0.6, py: '1px',
                        }}
                      >
                        <Typography sx={{ fontSize: '9px', lineHeight: 1 }}>{jp ? '♃' : '⚠'}</Typography>
                        <Typography sx={{
                          fontSize: '0.5rem', fontWeight: 700, lineHeight: 1,
                          color: jp ? '#15803d' : affliction.severity === 'critical' ? '#b91c1c' : affliction.severity === 'severe' ? '#c2410c' : '#b45309',
                        }}>
                          {jp ? `${nth} from own ♃` : `Lord in ${nth} from own`}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              </Box>

              {/* ── Body: placement (left) + bars (right) ──────────── */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                px: 1.5, py: 1.25, gap: { xs: 1.5, sm: 0 },
              }}>
                {/* LEFT — placement info */}
                <Box sx={{
                  flex: 1,
                  pr: { sm: 2 },
                  borderRight: { sm: `1px solid ${theme.light}` },
                  display: 'flex', flexDirection: 'column', gap: 0.75,
                }}>
                  {/* House name + keywords */}
                  {meaning && (
                    <Box sx={{ pb: 0.25, mb: 0.25, borderBottom: `1px dashed ${theme.light}` }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: eStyle.text, lineHeight: 1.3 }}>
                        {meaning.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.58rem', color: '#9ca3af', fontStyle: 'italic', lineHeight: 1.3 }}>
                        {meaning.keywords}
                      </Typography>
                    </Box>
                  )}

                  {/* Lord */}
                  {lordName !== '—' && (
                    <Box>
                      <Typography sx={{ fontSize: '0.52rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.15 }}>Lord</Typography>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1d4ed8' }}>
                        {PLANET_ABBREV[lordName] ?? lordName}{lordHouse ? ` → H${lordHouse}` : ''}
                      </Typography>
                    </Box>
                  )}

                  {/* Ascendant cusp (H1 only) */}
                  {cuspDeg && (
                    <Box>
                      <Typography sx={{ fontSize: '0.52rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.15 }}>Ascendant</Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 700, color: '#5B21B6' }}>{cuspDeg}</Typography>
                    </Box>
                  )}

                  {/* Residents */}
                  {residents.length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: '0.52rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>Residents</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {residents.map(pl => {
                          const fc = getFuncColor(pl.functionalNature || 'neutral');
                          const pctColor = (pl.blendedPct ?? 0) >= 0 ? '#15803D' : '#B91C1C';
                          return (
                            <Box key={pl.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <Typography sx={{ px: 0.6, py: 0.05, borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700, color: '#fff', bgcolor: fc, lineHeight: 1.7 }}>
                                {PLANET_ABBREV[pl.name] ?? pl.name.slice(0, 2)}
                              </Typography>
                              {pl.blendedPct !== undefined && (
                                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: pctColor, bgcolor: `${pctColor}14`, px: 0.4, py: 0.05, borderRadius: '3px', border: '1px solid', borderColor: `${pctColor}40`, lineHeight: 1.6 }}>
                                  {pl.blendedPct > 0 ? '+' : ''}{Math.round(pl.blendedPct)}%
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Aspectors */}
                  {aspectors.length > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: '0.52rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>Aspects</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                        {aspectors.map((asp, i) => {
                          const fc = getFuncColor(asp.nature);
                          const kindShort = asp.kind.replace(' aspect', '');
                          return (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                              <Typography sx={{ px: 0.6, py: 0.05, borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700, color: fc, bgcolor: `${fc}14`, border: `1px solid ${fc}55`, lineHeight: 1.7 }}>
                                {PLANET_ABBREV[asp.planet] ?? asp.planet.slice(0, 2)}
                              </Typography>
                              <Typography sx={{ fontSize: '0.5rem', color: '#9ca3af' }}>{kindShort}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {residents.length === 0 && aspectors.length === 0 && (
                    <Typography sx={{ fontSize: '0.62rem', color: '#d1d5db', fontStyle: 'italic' }}>— Empty house</Typography>
                  )}
                </Box>

                {/* RIGHT — 4 metric bars */}
                <Box sx={{ width: { xs: '100%', sm: 210 }, pl: { sm: 2 }, flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {bars.map(({ label, value, displayLabel, isBipolar, scaleMin, scaleMax }) => {
                      if (value === null) return null;
                      const pct = Math.max(0, Math.min(100, value));
                      return (
                        <Box key={label}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
                            <Typography sx={{ fontSize: '0.5rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {label}
                            </Typography>
                            <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, color: isBipolar ? '#6b7280' : theme.text }}>
                              {displayLabel}
                            </Typography>
                          </Box>

                          {!isBipolar ? (
                            /* Standard left-to-right bar (Bhava Bala) */
                            <Box sx={{ height: '4px', borderRadius: '2px', background: theme.barBg, overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${theme.border}, ${theme.accent})`, borderRadius: '2px' }} />
                            </Box>
                          ) : (
                            /* Bipolar bar: center = 0, left = negative (red), right = positive (green) */
                            <>
                              <Box sx={{ height: '4px', borderRadius: '2px', background: '#f3f4f6', position: 'relative', overflow: 'hidden' }}>
                                <Box sx={{ position: 'absolute', left: '50%', top: 0, width: '1px', height: '100%', background: '#d1d5db', zIndex: 1 }} />
                                {Math.abs(pct - 50) > 0 && (
                                  <Box sx={{
                                    position: 'absolute', top: 0, height: '100%', borderRadius: '2px',
                                    ...(pct > 50
                                      ? { left: '50%', width: `${pct - 50}%`, background: '#16a34a' }
                                      : { right: '50%', width: `${50 - pct}%`, background: '#dc2626' }
                                    ),
                                  }} />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '1px' }}>
                                <Typography sx={{ fontSize: '0.38rem', color: '#d1d5db', lineHeight: 1 }}>{scaleMin}</Typography>
                                <Typography sx={{ fontSize: '0.38rem', color: '#d1d5db', lineHeight: 1 }}>0</Typography>
                                <Typography sx={{ fontSize: '0.38rem', color: '#d1d5db', lineHeight: 1 }}>{scaleMax}</Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>

              {/* ── Notes: lord-in-house + sign note ───────────────── */}
              {hasNotes && (
                <Box sx={{ px: 1.5, pb: 1, pt: 0.5, borderTop: `1px solid ${theme.light}`, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {lihPosition && (
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: eStyle.accent }}>
                      {lihPosition}
                    </Typography>
                  )}
                  {lihNote && (
                    <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: 1.55, fontStyle: 'italic' }}>
                      {lihNote}
                    </Typography>
                  )}
                  {signNote && (
                    <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: 1.55, fontStyle: 'italic', borderTop: lihNote ? '1px dashed #e5e7eb' : 'none', pt: lihNote ? 0.5 : 0 }}>
                      {signNote}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── Legends ───────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        {/* Element legend */}
        {(['fire', 'earth', 'air', 'water'] as const).map(el => (
          <Box key={el} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: ELEMENT_STYLE[el].accent }} />
            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'capitalize' }}>
              {ELEMENT_STYLE[el].glyph} {el}
            </Typography>
          </Box>
        ))}
        {/* Planet nature legend */}
        {([['#16a34a', 'Benefic/YK'], ['#dc2626', 'Malefic'], ['#d4b106', 'Neutral']] as [string, string][]).map(([color, label]) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color }} />
            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{label}</Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: '0.62rem', color: '#9ca3af', fontStyle: 'italic' }}>
          Solid pill = resident · Outlined pill = aspector · % = blended Shadbala strength
        </Typography>
      </Box>
    </ReportSection>
  );
};
